import { HttpClient } from '@angular/common/http';
import { StatutLimiteDemoModel } from 'src/models/pilotage/statutLimiteDemoModel';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { ScormPackagePersoPilotageModel } from 'src/models/pilotage/scormPackagePersoPilotageModel';
import { Observable } from 'rxjs';
import { UtilisateurService } from './utilisateur.service';
import { PackagePersoPilotageModel } from 'src/models/pilotage/packagePersoPilotageModel';
import { PackagePersoEditModel } from 'src/models/pilotage/packagePersoEditModel';
import { PackageDetailModel, PackageInfoUpdateModel } from 'src/models/pilotage/packageDetailModel';
import { LienUploadVideoModel } from 'src/models/pilotage/lienUploadVideoModel';
import { VideoStatusModel } from 'src/models/videoStatusModel';
import { VimeoIdValidationModel } from 'src/models/vimeoIdValidationModel';

@Injectable({
  providedIn: 'root'
})
export class PackagesPersoService {

  private baseUrl = environment.api_LMS + 'PackagePerso';

  constructor(
    private http: HttpClient,
    private utilisateurService: UtilisateurService
  ) { }

  obtenirPackagePersos(): Observable<ScormPackagePersoPilotageModel[]> {
    return this.http.get<ScormPackagePersoPilotageModel[]>(`${this.baseUrl}/ObtenirPackagePersos`, {
      params: {
        entiteId: this.utilisateurService.entiteUtilisateurSelectionnee.entiteId.toString(),
        utilisateurId: this.utilisateurService.entiteUtilisateurSelectionnee.utilisateurId.toString()
      }
    });
  }

  createPackage(packageData: PackagePersoPilotageModel): Observable<CreatePackageResponse> {
    const formData = this.buildPackageFormData(packageData);

    return this.http.post<CreatePackageResponse>(
      `${this.baseUrl}/CreerPackage`,
      formData
    );
  }

  updatePackage(packageId: number, packageData: PackagePersoPilotageModel): Observable<CreatePackageResponse> {
    const formData = this.buildPackageFormData(packageData);

    return this.http.post<CreatePackageResponse>(
      `${this.baseUrl}/UpdatePackage`,
      formData,
      {
        params: {
          packageId: packageId.toString(),
          utilisateurId: this.utilisateurService.entiteUtilisateurSelectionnee.utilisateurId.toString()
        }
      }
    );
  }

  getPackageForEdit(packageId: number): Observable<PackagePersoEditModel> {
    return this.http.get<PackagePersoEditModel>(`${this.baseUrl}/GetPackageForEdit`, {
      params: {
        packageId: packageId.toString()
      }
    });
  }

  private buildPackageFormData(packageData: PackagePersoPilotageModel): FormData {
    const formData = new FormData();

    if (packageData.thumbnail) {
      formData.append('thumbnail', packageData.thumbnail, packageData.thumbnail.name);
    }
    if (packageData.pdfFileFR) {
      formData.append('pdfFileFR', packageData.pdfFileFR, packageData.pdfFileFR.name);
    }
    if (packageData.pdfFileEN) {
      formData.append('pdfFileEN', packageData.pdfFileEN, packageData.pdfFileEN.name);
    }
    if (packageData.pdfFileES) {
      formData.append('pdfFileES', packageData.pdfFileES, packageData.pdfFileES.name);
    }

    if (packageData.subtitleFileFR) {
      formData.append('subtitleFileFR', packageData.subtitleFileFR, packageData.subtitleFileFR.name);
    }
    if (packageData.subtitleFileEN) {
      formData.append('subtitleFileEN', packageData.subtitleFileEN, packageData.subtitleFileEN.name);
    }
    if (packageData.subtitleFileES) {
      formData.append('subtitleFileES', packageData.subtitleFileES, packageData.subtitleFileES.name);
    }

    packageData.questions.forEach(question => {
      if (question.imageFR) {
        formData.append(`questionImage_${question.id}_FR`, question.imageFR, question.imageFR.name);
      }
      if (question.imageEN) {
        formData.append(`questionImage_${question.id}_EN`, question.imageEN, question.imageEN.name);
      }
      if (question.imageES) {
        formData.append(`questionImage_${question.id}_ES`, question.imageES, question.imageES.name);
      }
    });

    (packageData.attachedDocuments || []).forEach(doc => {
      if (doc.file) {
        formData.append(`attachedDocument_${doc.id}`, doc.file, doc.file.name);
      }
    });

    const packageDataWithoutFiles = {
      ...packageData,
      entiteId: this.utilisateurService.entiteUtilisateurSelectionnee.entiteId,
      utilisateurId: this.utilisateurService.entiteUtilisateurSelectionnee.utilisateurId,
      pdfFileFR: null,
      pdfFileEN: null,
      pdfFileES: null,
      subtitleFileFR: null,
      subtitleFileEN: null,
      subtitleFileES: null,
      thumbnail: null,
      entiteIds: packageData.entiteIds || [],
      groupeId: packageData.groupeId,
      videos: packageData.videos.map(video => ({
        id: video.id,
        languages: video.languages,
        videoFile: null,
        vimeoId: video.vimeoId
      })),
      questions: packageData.questions.map(question => ({
        ...question,
        imageFR: null,
        imageEN: null,
        imageES: null
      })),
      attachedDocuments: (packageData.attachedDocuments || []).map(doc => ({
        id: doc.id,
        titre: doc.titre,
        fileName: doc.file ? doc.file.name : doc.fileName
      })),
      pourcentageSucces: packageData.pourcentageSucces
    };

    formData.append('packageData', JSON.stringify(packageDataWithoutFiles));

    return formData;
  }


  deletePackage(packageId: number): Observable<void> {
    const userId = this.utilisateurService.entiteUtilisateurSelectionnee.utilisateurId;

    return this.http.delete<void>(`${this.baseUrl}/SupprimerPackage`, {
      params: {
        packageId: packageId.toString(),
        entiteId: this.utilisateurService.entiteUtilisateurSelectionnee.entiteId.toString(),
        utilisateurId: userId.toString()
      }
    });
  }

  togglePackageActivation(packageId: number, actif: boolean): Observable<void> {
    const userId = this.utilisateurService.entiteUtilisateurSelectionnee.utilisateurId;

    return this.http.put<void>(`${this.baseUrl}/ModifierStatutPackage`, null, {
      params: {
        packageId: packageId.toString(),
        actif: actif.toString(),
        entiteId: this.utilisateurService.entiteUtilisateurSelectionnee.entiteId.toString(),
        utilisateurId: userId.toString()
      }
    });
  }

  obtenirStatutLimiteDemo(packageId: number): Observable<StatutLimiteDemoModel> {
    return this.http.get<StatutLimiteDemoModel>(`${this.baseUrl}/ObtenirStatutLimiteDemo`, {
      params: {
        packageId: packageId.toString()
      }
    });
  }

  getPackageDetails(packageId: number): Observable<PackageDetailModel> {
    return this.http.get<PackageDetailModel>(`${this.baseUrl}/ObtenirDetailPackage`, {
      params: {
        packageId: packageId.toString()
      }
    });
  }

  updatePackageInfo(packageId: number, packageInfo: PackageInfoUpdateModel): Observable<void> {
    const userId = this.utilisateurService.entiteUtilisateurSelectionnee.utilisateurId;

    return this.http.put<void>(`${this.baseUrl}/ModifierInfoPackage`, packageInfo, {
      params: {
        packageId: packageId.toString(),
        utilisateurId: userId.toString()
      }
    });
  }

  initierUploadVideo(nom: string, tailleFichier: number, utilisateurId: number): Observable<LienUploadVideoModel> {
    return this.http.post<LienUploadVideoModel>(`${this.baseUrl}/InitierUploadVideo`, {
      nom,
      tailleFichier,
      utilisateurId
    });
  }

  getVideoStatus(vimeoId: string): Observable<VideoStatusModel> {
    return this.http.get<VideoStatusModel>(`${this.baseUrl}/VideoStatus`, {
      params: { vimeoId }
    });
  }

  validateVimeoId(vimeoId: string, packageId: number): Observable<VimeoIdValidationModel> {
    return this.http.get<VimeoIdValidationModel>(`${this.baseUrl}/ValidateVimeoId`, {
      params: {
        vimeoId,
        packageId: packageId.toString()
      }
    });
  }

}

export interface CreatePackageResponse {
  scormPackageId?: number;
}

export type LanguageDto = 0 | 1 | 2 | 'fr' | 'en' | 'es';

export interface QuizDto {
  quizId: number;
  scormPackageId: number;
  picturesRelativePath: string | null;
  isAnswersAreRandom: boolean;
  isAutoValidateSingleSelection: boolean;
}

export interface QuizTitleDto {
  language: LanguageDto;
  titleHtml: string | null;
}

export interface QuizPosterFrameDto {
  language: LanguageDto;
  fileName: string | null;
}

export interface QuizIntroMessageDto {
  language: LanguageDto;
  titleHtml: string | null;
  subtitleHtml: string | null;
}

export interface QuizOutroMessageTextDto {
  language: LanguageDto;
  titleHtml: string;
  subtitleHtml: string;
  continueButtonHtml: string;
}

export interface QuizOutroMessageDto {
  scoreMax: number;
  sortOrder: number;
  quizOutroMessageTextDtos: QuizOutroMessageTextDto[];
}

export interface QuizQuestionTextDto {
  language: LanguageDto;
  questionHtml: string;
}

export interface QuizQuestionAnswerTextDto {
  language: LanguageDto;
  answerHtml: string;
}

export interface QuizQuestionAnswerDto {
  isCorrect: boolean;
  sortOrder: number;
  quizQuestionAnswerTextDtos: QuizQuestionAnswerTextDto[];
}

export interface QuizQuestionDto {
  questionIdentifier: string;
  isAnswersAreRandom: boolean;
  isMultiSelection: boolean;
  sortOrder: number;
  quizQuestionTextDtos: QuizQuestionTextDto[];
  quizQuestionAnswerDtos: QuizQuestionAnswerDto[];
}

export interface GetQuizInfosFromJsonResponse {
  quizPath: string;
  quizDto: QuizDto;
  quizTitleDtos: QuizTitleDto[];
  quizPosterFrameDtos: QuizPosterFrameDto[];
  quizIntroMessageDtos: QuizIntroMessageDto[];
  quizOutroMessageDtos: QuizOutroMessageDto[];
  quizQuestionDtos: QuizQuestionDto[];
}
